import { useEffect, useState } from 'react';

import { useApi, type StateListResponse, type StateSetting } from './client';

/**
 * Loads per-state customisations (colour + custom SVG) from
 * /api/v1/admin/states. The hook is used in two places:
 *
 *   - StateIcon — renders the custom SVG if present, else the bundled
 *                 default for the state.
 *   - GlobalStateStyles — injects a <style> tag in the document head
 *                 that overrides the --state-{id} CSS custom property
 *                 with the admin-chosen colour.
 *
 * No caching beyond React state; the hook fires the GET on mount.
 */

export type StateSettingsMap = Map<StateSetting['state_id'], StateSetting>;

type HookState =
  | { kind: 'loading' }
  | { kind: 'ok'; map: StateSettingsMap }
  | { kind: 'error'; message: string };

export function useStateSettings(): HookState {
  const callApi = useApi();
  const [state, setState] = useState<HookState>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<StateListResponse>('/admin/states');
        if (cancelled) return;
        const map: StateSettingsMap = new Map();
        for (const row of data.rows) {
          map.set(row.state_id, row);
        }
        setState({ kind: 'ok', map });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load state settings';
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
